/**
 * Lift2Fit — admin: Overview (`#/admin`).
 *
 * The platform at a glance, covering both interfaces: people, businesses by
 * plan, seats used against capacity, activity, and the money. Every figure is a
 * count or a sum from `GET /admin/summary` — there is no per-account read here
 * and nothing private on screen.
 */
import { api } from '@/lib/api';
import { formatCompactCount, formatCompactMoney, formatCount, formatDate } from '@/lib/format';
import { AlertIcon, MonitorIcon, ShieldIcon, TargetIcon, TrendUpIcon } from '@/lib/icons';
import {
  AppLinkButton,
  EmptyState,
  LoadingPanel,
  MetricTile,
  Panel,
  ScreenHeader,
  StatusChip,
} from '../components';
import { formatMoney } from '../crm/shared';
import { useAsync } from '../hooks';
import { Link } from '../router';
import { ADMIN_EYEBROW, AdminStat, coachPlanLabel, coachPlanSize, seatLimitLabel } from './shared';

export function AdminOverviewScreen() {
  const state = useAsync(() => api.adminSummary(), []);
  const summary = state.data;

  if (state.loading && !summary) {
    return (
      <div className="flex flex-col gap-4">
        <LoadingPanel label="Loading the platform summary" rows={4} />
        <LoadingPanel label="Loading revenue" rows={3} />
      </div>
    );
  }

  if (!summary) {
    return (
      <EmptyState
        title="We could not read the platform summary"
        body={state.error ?? 'The admin API did not answer. Sign in as an administrator and try again.'}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
        action={
          <AppLinkButton to="/dashboard" variant="outline">
            Back to your dashboard
          </AppLinkButton>
        }
      />
    );
  }

  const { accounts, activity, sessions, sets, blocks, seats, revenue } = summary;

  return (
    <div className="flex flex-col gap-6" data-testid="admin-overview">
      <ScreenHeader
        eyebrow={ADMIN_EYEBROW}
        title="Overview"
        badge={<StatusChip tone="danger">Founder only</StatusChip>}
        lead="Both interfaces at a glance: accounts, coaching businesses, seats, usage and money. Aggregates only — the admin never reads a set or a client roster."
        actions={
          <AppLinkButton to="/admin/accounts">
            <ShieldIcon className="text-[14px]" />
            Accounts
          </AppLinkButton>
        }
      />

      <section aria-label="People" data-testid="admin-overview-people" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricTile
          label="Accounts"
          value={formatCompactCount(accounts.total)}
          valueTitle={formatCount(accounts.total)}
          hint="every role, both products"
          icon={<TargetIcon />}
        />
        <MetricTile label="Lifters" value={formatCompactCount(accounts.lifters)} valueTitle={formatCount(accounts.lifters)} hint="hold the lifter role" />
        <MetricTile
          label="Coaches & businesses"
          value={formatCompactCount(accounts.coaches)}
          valueTitle={formatCount(accounts.coaches)}
          hint="hold the coach role"
          hintTone="accent"
        />
        <MetricTile
          label="Admins"
          value={formatCompactCount(accounts.admins)}
          valueTitle={formatCount(accounts.admins)}
          hint="this surface's role"
          hintTone="danger"
        />
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel
          title="Activity"
          subtitle="Distinct accounts with any recorded activity in the window."
          testId="admin-overview-activity"
        >
          <div className="grid grid-cols-2 gap-3">
            <AdminStat label="Active · 7 days" value={formatCompactCount(activity.active7)} valueTitle={formatCount(activity.active7)} />
            <AdminStat label="Active · 28 days" value={formatCompactCount(activity.active28)} valueTitle={formatCount(activity.active28)} />
            <AdminStat
              label="Sessions logged"
              value={formatCompactCount(sessions.completed)}
              valueTitle={formatCount(sessions.completed)}
              hint={`${formatCount(sessions.completed28)} in the last 28 days`}
            />
            <AdminStat
              label="Sets logged"
              value={formatCompactCount(sets.logged)}
              valueTitle={formatCount(sets.logged)}
              hint={`${formatCount(sets.logged28)} in the last 28 days`}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <AdminStat
              label="Blocks completed"
              value={formatCompactCount(blocks.completed)}
              valueTitle={formatCount(blocks.completed)}
              hint="4-week blocks that have ended"
            />
            <AdminStat
              label="Seats used"
              value={`${formatCompactCount(seats.used)} / ${seats.capacity === null ? 'unlimited' : formatCompactCount(seats.capacity)}`}
              valueTitle={`${formatCount(seats.used)} / ${seatLimitLabel(seats.capacity)}`}
              hint={
                seats.unlimitedBusinesses > 0
                  ? `${seats.unlimitedBusinesses} unlimited business(es)`
                  : 'active clients + live invites'
              }
            />
          </div>
        </Panel>

        <Panel
          title="Money"
          subtitle="From the payments ledger, plus the active subscriptions valued at list price."
          testId="admin-overview-money"
        >
          <div className="grid grid-cols-2 gap-3">
            <AdminStat
              label="Paid"
              value={formatCompactMoney(revenue.paid, revenue.currency)}
              valueTitle={formatMoney(revenue.paid, revenue.currency)}
              hint={`${formatCount(revenue.paidCount)} payment(s)`}
              tone="text-success"
              valueTestId="admin-overview-paid"
            />
            <AdminStat
              label="Due"
              value={formatCompactMoney(revenue.due, revenue.currency)}
              valueTitle={formatMoney(revenue.due, revenue.currency)}
              hint={`${formatCount(revenue.dueCount)} outstanding`}
              tone="text-warning"
              valueTestId="admin-overview-due"
            />
            <AdminStat
              label="Refunded"
              value={formatCompactMoney(revenue.refunded, revenue.currency)}
              valueTitle={formatMoney(revenue.refunded, revenue.currency)}
              hint={`${formatCount(revenue.refundedCount)} refund(s)`}
              valueTestId="admin-overview-refunded"
            />
            <AdminStat
              label="MRR"
              value={formatCompactMoney(revenue.mrr, revenue.currency)}
              valueTitle={formatMoney(revenue.mrr, revenue.currency)}
              hint={`${formatCount(revenue.activeSubscriptions)} active subscription(s)`}
              tone="text-accent"
              valueTestId="admin-overview-mrr"
            />
          </div>
          <div className="flex flex-col gap-2" data-testid="admin-overview-subscriptions">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
              Subscriptions by plan
            </span>
            <div className="grid grid-cols-2 gap-2">
              {revenue.subscriptions.byPlan.length === 0 ? (
                <span className="text-[12px] text-ink-muted">No catalog plan is active.</span>
              ) : (
                revenue.subscriptions.byPlan.map((entry) => (
                  <span
                    key={`${entry.audience}-${entry.planKey}`}
                    className="flex items-baseline justify-between gap-3 border-b border-hairline py-1 text-[12px]"
                  >
                    <span className="text-ink-muted">
                      {entry.label} <span className="uppercase tracking-[0.06em]">{entry.audience}</span>
                    </span>
                    <span className="l2f-tabular font-mono text-ink">
                      {formatCount(entry.count)} · {formatMoney(entry.mrr, revenue.currency)}
                    </span>
                  </span>
                ))
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[12px] text-ink-muted">
            <span className="inline-flex items-center gap-1.5">
              <TrendUpIcon className="text-[13px]" />
              {revenue.customContracts > 0
                ? `${formatCount(revenue.customContracts)} Scale contract(s) negotiated — not in MRR`
                : 'No negotiated contracts'}
            </span>
            <Link to="/admin/revenue" className="font-bold uppercase tracking-[0.08em] text-accent hover:text-accent-hover">
              Open revenue
            </Link>
          </div>
        </Panel>
      </div>

      <Panel
        title="Businesses by plan"
        subtitle="One entity sized by client count: Free is the wedge, Scale is negotiated."
        testId="admin-overview-plans"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/admin/plans"
              className="inline-flex h-9 items-center gap-2 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
            >
              <TrendUpIcon className="text-[14px]" />
              Plans & pricing
            </Link>
            <Link
              to="/admin/businesses"
              className="inline-flex h-9 items-center gap-2 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
            >
              <MonitorIcon className="text-[14px]" />
              Businesses
            </Link>
          </div>
        }
      >
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-hairline text-[10px] uppercase tracking-[0.1em] text-ink-muted">
              <th className="py-2 pr-3 font-bold">Plan</th>
              <th className="py-2 pr-3 font-bold">Covers</th>
              <th className="py-2 pr-3 font-bold">Businesses</th>
              <th className="py-2 pr-3 font-bold">Seats used</th>
              <th className="py-2 font-bold">Seat limit</th>
            </tr>
          </thead>
          <tbody>
            {summary.businessesByPlan.map((entry) => (
              <tr
                key={entry.plan}
                data-testid={`admin-plan-row-${entry.plan}`}
                className="border-b border-hairline last:border-b-0"
              >
                <td className="py-2 pr-3 text-[13px] font-bold text-ink">{coachPlanLabel(entry.plan)}</td>
                <td className="py-2 pr-3 text-[12px] text-ink-muted">{coachPlanSize(entry.plan)}</td>
                <td className="l2f-tabular py-2 pr-3 font-mono text-[13px] text-ink">{formatCount(entry.businesses)}</td>
                <td className="l2f-tabular py-2 pr-3 font-mono text-[13px] text-ink">{formatCount(entry.seatsUsed)}</td>
                <td className="l2f-tabular py-2 font-mono text-[13px] text-ink-muted">{seatLimitLabel(entry.seatsLimit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <p className="text-[12px] text-ink-muted">
        Summary generated {formatDate(summary.generatedAt)} · activity counts completed sessions, sign-ins, bookings and ledger writes.
      </p>
    </div>
  );
}
